import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getContractApiUrl, isProductionEnvironment } from '../utils/apiUrls';

// Backend URL configuration
const BACKEND_URL = getContractApiUrl();

// Timeline-only columns in order
const TIMELINE_COLUMNS = [
  'id',
  'property_address',
  'purchase_agreement_signed_by_buyer',
  'purchase_agreement_signed_by_seller',
  'fully_executed_date',
  'purchase_agreement_time_reference_date',
  'earnest_money_deposit_days',
  'earnest_money_deposit_due_date',
  'earnest_money_deposit_due_date_original',
  'close_of_escrow_days',
  'close_of_escrow_due_date',
  'close_of_escrow_due_date_original',
  'loan_contingency',
  'loan_contingency_due_date',
  'appraisal_contingency',
  'appraisal_contingency_days',
  'appraisal_contingency_due_date',
  'inspection_contingency',
  'inspection_contingency_days',
  'inspection_contingency_due_date',
  'buyer_property_sale_contingency',
  'buyer_property_sale_contingency_days',
  'buyer_property_sale_contingency_due_date',
  'title_review_contingency',
  'title_review_contingency_days',
  'title_review_contingency_due_date',
  'insurance_contingency',
  'insurance_contingency_days',
  'insurance_contingency_due_date',
  'seller_disclosures_contingency',
  'seller_disclosures_contingency_days',
  'seller_disclosures_contingency_due_date',
  'leased_or_liened_items_contingency',
  'leased_or_liened_items_contingency_days',
  'leased_or_liened_items_contingency_due_date',
  'hoa_contingency',
  'hoa_contingency_days',
  'hoa_contingency_due_date',
  'rental_property_accounting_contingency',
  'rental_property_accounting_contingency_days',
  'rental_property_accounting_contingency_due_date',
  'buyer_proof_of_funds_days',
  'buyer_proof_of_funds_due_date',
  'buyer_final_walk_through_days',
  'buyer_final_walk_through_due_date'
];

function DeveloperContracts() {
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [pageSize, setPageSize] = useState(20);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [timelineOnly, setTimelineOnly] = useState(false);
  const [showDevMenu, setShowDevMenu] = useState(false);

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isProd = isProductionEnvironment();

  // Filters
  const [propertyAddress, setPropertyAddress] = useState('');
  const [contractId, setContractId] = useState('');
  const [uploadDateStart, setUploadDateStart] = useState('');
  const [uploadDateEnd, setUploadDateEnd] = useState('');
  const [purchaseAgreementDateStart, setPurchaseAgreementDateStart] = useState('');
  const [purchaseAgreementDateEnd, setPurchaseAgreementDateEnd] = useState('');
  const [closeOfEscrowDateStart, setCloseOfEscrowDateStart] = useState('');
  const [closeOfEscrowDateEnd, setCloseOfEscrowDateEnd] = useState('');

  const isKeyFloUser = useMemo(() => {
    const email = user?.email || '';
    const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];
    return ALLOWED_USERS.includes(email);
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isKeyFloUser) {
      navigate('/');
    }
  }, [isAuthenticated, isKeyFloUser, navigate]);

  // Display date strings as-is without timezone conversion
  const formatDateTime = (ts) => {
    if (!ts) return '';
    // If it's already a string, return it as-is
    if (typeof ts === 'string') return ts;
    // Only format if it's actually a Date object
    try {
      return ts.toISOString();
    } catch (e) {
      return String(ts);
    }
  };

  // Display date strings as YYYY-MM-DD format without timezone conversion
  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return '';
      // If already in YYYY-MM-DD format, return as-is (no timezone conversion)
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      // If it's a date-only string in YYYY-MM-DD format, parse components directly to avoid timezone issues
      const dateMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
      }
      // Otherwise, parse and format as YYYY-MM-DD (for other date formats)
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr || '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return dateStr || '';
    }
  };

  const buildParams = (resetOffset = true) => {
    const params = {
      limit: pageSize,
      offset: resetOffset ? 0 : offset
    };

    if (propertyAddress) params.property_address = propertyAddress;
    if (contractId) params.contract_id = contractId;
    if (uploadDateStart) params.upload_date_start = new Date(uploadDateStart).toISOString();
    if (uploadDateEnd) {
      const end = new Date(uploadDateEnd);
      end.setHours(23, 59, 59, 999);
      params.upload_date_end = end.toISOString();
    }
    if (purchaseAgreementDateStart) params.purchase_agreement_time_reference_date_start = purchaseAgreementDateStart;
    if (purchaseAgreementDateEnd) params.purchase_agreement_time_reference_date_end = purchaseAgreementDateEnd;
    if (closeOfEscrowDateStart) params.close_of_escrow_due_date_start = closeOfEscrowDateStart;
    if (closeOfEscrowDateEnd) params.close_of_escrow_due_date_end = closeOfEscrowDateEnd;

    return params;
  };

  const fetchContracts = async (reset = true) => {
    if (!isAuthenticated || !isKeyFloUser) return;
    try {
      setLoading(true);
      setError('');

      const params = buildParams(reset);
      const response = await axios.get(`${BACKEND_URL}/contracts/dev`, { params });

      if (response.data?.status !== 'success') {
        throw new Error(response.data?.error || 'Failed to fetch contracts');
      }

      const newContracts = response.data.contracts || [];
      setContracts(reset ? newContracts : [...contracts, ...newContracts]);
      setTotalCount(response.data.total_count || 0);
      setOffset((reset ? 0 : offset) + newContracts.length);
    } catch (e) {
      setError(e.message || 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isKeyFloUser) fetchContracts(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isKeyFloUser, pageSize]);

  // Get all column names from the first contract (if available)
  const getAllColumns = () => {
    if (contracts.length === 0) return [];
    const firstContract = contracts[0];
    return Object.keys(firstContract).sort();
  };

  const getDisplayColumns = () => {
    let columns;
    if (timelineOnly) {
      columns = TIMELINE_COLUMNS;
    } else {
      columns = getAllColumns();
    }

    // Ensure 'id' and 'property_address' are always the first two columns
    const orderedColumns = [];
    const otherColumns = [];

    // First, add 'id' if it exists
    if (columns.includes('id')) {
      orderedColumns.push('id');
    }

    // Then, add 'property_address' if it exists
    if (columns.includes('property_address')) {
      orderedColumns.push('property_address');
    }

    // Add all other columns (excluding 'id' and 'property_address' which we already added)
    columns.forEach(col => {
      if (col !== 'id' && col !== 'property_address') {
        otherColumns.push(col);
      }
    });

    // Return ordered columns: id, property_address, then all others
    return [...orderedColumns, ...otherColumns];
  };

  const handleDeleteClick = (contract) => {
    setContractToDelete(contract);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!contractToDelete) return;
    try {
      setDeleting(true);
      const response = await axios.delete(`${BACKEND_URL}/contracts/${contractToDelete.id}`);
      if (response.data?.error) {
        throw new Error(response.data.error);
      }
      setContracts((prev) => prev.filter((c) => c.id !== contractToDelete.id));
      setTotalCount((prev) => prev - 1);
    } catch (e) {
      alert(e.response?.data?.error || e.message || 'Failed to delete contract');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setContractToDelete(null);
    }
  };

  if (!isAuthenticated) {
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
      <p>Please log in</p>
      <button onClick={() => loginWithRedirect({
        authorizationParams: {
          prompt: 'login'
        }
      })}>Log in</button>
    </div>
  }

  if (!isKeyFloUser) {
    return (
      <div style={{ padding: '24px' }}>
        <h2>Access Denied</h2>
        <p>This page is restricted to KeyFlo team members.</p>
      </div>
    );
  }

  const displayColumns = getDisplayColumns();

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2>Developer • Contracts</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
          <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
          <label style={{ marginRight: '8px' }}>Page size:</label>
          <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <label style={{ marginLeft: '16px', marginRight: '8px' }}>
            <input
              type="checkbox"
              checked={timelineOnly}
              onChange={(e) => setTimelineOnly(e.target.checked)}
              style={{ marginRight: '4px' }}
            />
            Timeline Only
          </label>
          <DeveloperNavigation />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '12px', marginBottom: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Property Address</label>
          <input value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} placeholder="property_address" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Contract ID</label>
          <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="contract_id" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Upload Date Start</label>
          <input type="date" value={uploadDateStart} onChange={(e) => setUploadDateStart(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Upload Date End</label>
          <input type="date" value={uploadDateEnd} onChange={(e) => setUploadDateEnd(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Purchase Agreement Date Start</label>
          <input type="date" value={purchaseAgreementDateStart} onChange={(e) => setPurchaseAgreementDateStart(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Purchase Agreement Date End</label>
          <input type="date" value={purchaseAgreementDateEnd} onChange={(e) => setPurchaseAgreementDateEnd(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Close of Escrow Date Start</label>
          <input type="date" value={closeOfEscrowDateStart} onChange={(e) => setCloseOfEscrowDateStart(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Close of Escrow Date End</label>
          <input type="date" value={closeOfEscrowDateEnd} onChange={(e) => setCloseOfEscrowDateEnd(e.target.value)} style={{ width: '100%' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button onClick={() => { setOffset(0); fetchContracts(true); }}>Apply Filters</button>
        <button onClick={() => {
          setPropertyAddress('');
          setContractId('');
          setUploadDateStart('');
          setUploadDateEnd('');
          setPurchaseAgreementDateStart('');
          setPurchaseAgreementDateEnd('');
          setCloseOfEscrowDateStart('');
          setCloseOfEscrowDateEnd('');
          setOffset(0);
          fetchContracts(true);
        }}>Clear</button>
        <button onClick={() => fetchContracts(false)}>Load More</button>
      </div>

      {loading && <LoadingSpinner />}
      {
        error && (
          <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>
        )
      }

      <div style={{ marginBottom: '12px' }}>
        Showing {contracts.length} of {totalCount} contracts
      </div>

      <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
        <table style={{ width: 'max-content', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', position: 'sticky', top: 0, left: 0, zIndex: 2, minWidth: 90, width: 90 }}>
                actions
              </th>
              {displayColumns.map((col, colIndex) => {
                // Freeze first two columns (id and property_address, which are always first)
                const isFrozen = colIndex === 0 || colIndex === 1;
                // Offset by actions column width (90px)
                const leftValue = colIndex === 0 ? 90 : 190;
                const columnWidth = isFrozen ? (colIndex === 0 ? 100 : 250) : undefined;

                return (
                  <th
                    key={col}
                    style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderBottom: '1px solid #e5e7eb',
                      background: '#f8fafc',
                      position: isFrozen ? 'sticky' : 'relative',
                      top: 0,
                      left: isFrozen ? `${leftValue}px` : 'auto',
                      zIndex: isFrozen ? 2 : 1,
                      boxShadow: isFrozen && colIndex === 1 ? '2px 0 5px rgba(0,0,0,0.1)' : 'none',
                      width: isFrozen ? `${columnWidth}px` : undefined,
                      minWidth: isFrozen ? `${columnWidth}px` : 150,
                      maxWidth: isFrozen ? `${columnWidth}px` : undefined
                    }}
                  >
                    {col}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {contracts.map((contract, idx) => (
              <tr key={contract.id || idx} style={{ borderBottom: '1px solid #f1f5f9', height: '100px', maxHeight: '100px' }}>
                <td style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top', position: 'sticky', left: 0, zIndex: 1, background: '#ffffff', minWidth: 90, width: 90 }}>
                  <button
                    onClick={() => handleDeleteClick(contract)}
                    style={{
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    Delete
                  </button>
                </td>
                {displayColumns.map((col, colIndex) => {
                  let val = contract[col];

                  // Format dates and datetimes
                  if (col.includes('_date') || col === 'upload_date') {
                    if (col === 'upload_date') {
                      val = val ? formatDateTime(val) : '';
                    } else {
                      val = val ? formatDate(val) : '';
                    }
                  }

                  // Format boolean values
                  if (typeof val === 'boolean') {
                    val = val ? 'true' : 'false';
                  }

                  // Format numeric values
                  if (typeof val === 'number') {
                    val = val.toString();
                  }

                  if (val === null || typeof val === 'undefined') val = '';

                  // Freeze first two columns (id and property_address, which are always first)
                  const isFrozen = colIndex === 0 || colIndex === 1;
                  // Offset by actions column width (90px)
                  const leftValue = colIndex === 0 ? 90 : 190;
                  const columnWidth = isFrozen ? (colIndex === 0 ? 100 : 250) : undefined;

                  return (
                    <td
                      key={col}
                      style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid #f1f5f9',
                        borderRight: '1px solid #f1f5f9',
                        verticalAlign: 'top',
                        whiteSpace: isFrozen ? 'nowrap' : 'pre-wrap',
                        wordBreak: isFrozen ? 'normal' : 'break-word',
                        overflow: isFrozen ? 'hidden' : 'visible',
                        textOverflow: isFrozen ? 'ellipsis' : 'clip',
                        minWidth: isFrozen ? `${columnWidth}px` : 150,
                        width: isFrozen ? `${columnWidth}px` : undefined,
                        maxWidth: isFrozen ? `${columnWidth}px` : undefined,
                        position: isFrozen ? 'sticky' : 'relative',
                        left: isFrozen ? `${leftValue}px` : 'auto',
                        zIndex: isFrozen ? 1 : 0,
                        background: isFrozen ? '#ffffff' : 'transparent',
                        boxShadow: isFrozen && colIndex === 1 ? '2px 0 5px rgba(0,0,0,0.1)' : 'none',
                        boxSizing: 'border-box'
                      }}
                      title={isFrozen ? String(val) : undefined}
                    >
                      <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                        {String(val)}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            {contracts.length === 0 && !loading && (
              <tr>
                <td colSpan={displayColumns.length + 1} style={{ padding: '12px', textAlign: 'center' }}>No contracts found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {showDeleteModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '8px', width: '420px', maxWidth: '90%' }}>
            <h3 style={{ marginTop: 0, color: '#ef4444' }}>Confirm Delete</h3>
            {isProd && (
              <p style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '1rem', textAlign: 'center', border: '2px solid #dc2626', borderRadius: '4px', padding: '8px' }}>
                THIS IS THE PROD ENV. PLEASE BE 100% SURE YOU WANT TO DELETE.
              </p>
            )}
            <p>
              Are you sure you want to delete contract <strong>#{contractToDelete?.id}</strong>
              {contractToDelete?.property_address ? ` — ${contractToDelete.property_address}` : ''}?
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
              <button
                onClick={() => { setShowDeleteModal(false); setContractToDelete(null); }}
                style={{ background: '#f3f4f6', border: '1px solid #d1d5db', color: '#374151', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                style={{ background: '#ef4444', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
              >
                {deleting ? 'Deleting...' : 'Delete Contract'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div >
  );
}

export default DeveloperContracts;

